import React, { useMemo } from 'react'
import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
  Underline,
  FlipHorizontal,
  FlipVertical,
  RotateCw,
  Split,
  Scissors,
  ChevronRight
} from 'lucide-react'
import FitToPageButton from './FitToPageButton'
import AnimationPropertiesPanel from './AnimationPropertiesPanel'
import { extractPlaceholders } from '../utils/placeholderUtils'

/** 
 * Text Element Properties Component
 * Handles the properties panel for text and wordart elements
 */
const TextElementProperties = ({
  element,
  selectedElement,
  updateElementProperty,
  showAdvancedTextProperties,
  splitTextIntoWords,
  splitTextIntoCharacters,
  currentPage,
  onOpenFontsSidebar,
  onOpenAnimatePanel,
}) => {
  if (element.type !== 'text' && element.type !== 'wordart') {
    return null
  }

  // Handle partial text formatting
  const handlePartialFormatting = (format, value) => {
    const textEditor = window.currentTextEditor
    if (textEditor && textEditor.hasSelection && textEditor.applyFormatting) {
      textEditor.applyFormatting(format, value)
    } else {
      // Apply to entire element if no selection
      updateElementProperty(element.id, format, value)
    }
  }

  // Placeholder management
  const currentPlaceholders = useMemo(() => {
    return extractPlaceholders(element.content || '')
  }, [element.content])

  const elementPlaceholders = element.placeholders || []

  // Re-evaluate placeholders on blur
  const handleContentBlur = () => {
    const detectedPlaceholders = extractPlaceholders(element.content || '')

    // Create a map of existing placeholders
    const existingMap = {}
    elementPlaceholders.forEach(item => {
      if (item && item.key) {
        existingMap[item.key] = item.value || ''
      }
    })

    // Build new placeholders array - keep existing values for placeholders that still exist
    const newPlaceholders = detectedPlaceholders.map(key => {
      if (existingMap.hasOwnProperty(key)) {
        return { key, value: existingMap[key] }
      }
      return { key, value: '' }
    })

    // Update if changed
    const existingKeys = elementPlaceholders.map(p => p?.key).filter(Boolean).sort().join(',')
    const newKeys = newPlaceholders.map(p => p.key).sort().join(',')

    if (existingKeys !== newKeys || JSON.stringify(elementPlaceholders) !== JSON.stringify(newPlaceholders)) {
      updateElementProperty(selectedElement, 'placeholders', newPlaceholders)
    }
  }

  // Handle placeholder value change
  const handlePlaceholderChange = (placeholderKey, value) => {
    const updatedPlaceholders = currentPlaceholders.map(key => {
      if (key === placeholderKey) {
        return { key, value: value || '' }
      }
      // Keep existing value if it exists
      const existing = elementPlaceholders.find(p => p && p.key === key)
      return existing || { key, value: '' }
    })
    updateElementProperty(selectedElement, 'placeholders', updatedPlaceholders)
  }

  // Get placeholder value
  const getPlaceholderValue = (key) => {
    const found = elementPlaceholders.find(p => p && p.key === key)
    return found ? found.value : ''
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          <h3 className="text-sm font-semibold text-gray-800">Text Properties</h3>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Text Content - Enhanced */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Content</label>
          <textarea
            value={element.content || ''}
            onChange={(e) => updateElementProperty(selectedElement, 'content', e.target.value)}
            onBlur={handleContentBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={2}
            placeholder="Enter your text here..."
          />

          {/* Placeholder Input Fields */}
          {currentPlaceholders.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-gray-200">
              <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Placeholder Values</label>
              {currentPlaceholders.map((placeholderKey) => (
                <div key={placeholderKey} className="space-y-1">
                  <label className="block text-xs text-gray-600">{placeholderKey}</label>
                  <input
                    type="text"
                    value={getPlaceholderValue(placeholderKey)}
                    onChange={(e) => handlePlaceholderChange(placeholderKey, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Enter value for ${placeholderKey}...`}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Typography Section */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <div className="w-1 h-4 bg-green-500 rounded"></div>
            <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Typography</label>
          </div>

          {/* Font Family - Button to open sidebar */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Font Family - {element.fontFamily || ''}</label>
            <button
              type="button"
              onClick={() => {
                if (onOpenFontsSidebar) {
                  onOpenFontsSidebar(element.content || 'Hello World')
                }
              }}
              className="w-full px-3 py-2 text-left border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
            >
              <span
                className="text-sm truncate flex-1"
                style={{ fontFamily: `"${element.fontFamily || ''}", sans-serif` }}
              >
                {element.fontFamily || ''}
              </span>
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
            </button>
          </div>

          {/* Font Size and Weight Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Size</label>
              <input
                type="number"
                value={element.fontSize || 16}
                onChange={(e) => {
                  const fontSize = parseInt(e.target.value)
                  if (!fontSize || Number.isNaN(fontSize)) return
                  // If there's an ACTIVE inline-text selection, format just that
                  // selection (Canva-style partial sizing).
                  const ed = window.currentTextEditor
                  const hasLiveSelection = ed && ed.hasSelection && ed.applyFormatting
                  if (hasLiveSelection) {
                    ed.restoreSelection && ed.restoreSelection()
                    ed.applyFormatting('fontSize', fontSize)
                    return
                  }
                  // Otherwise set the element-level size. CRITICAL: also rewrite
                  // every richText span's fontSize so the per-span size (which
                  // both the editor and the worker read) does not override the
                  // element size — otherwise changing Size appears to do nothing.
                  updateElementProperty(selectedElement, 'fontSize', fontSize)
                  if (element.richText && Array.isArray(element.richText.spans) && element.richText.spans.length) {
                    const nextRich = {
                      ...element.richText,
                      spans: element.richText.spans.map((sp) => ({
                        ...sp,
                        style: { ...(sp.style || {}), fontSize: `${fontSize}px` },
                      })),
                    }
                    updateElementProperty(selectedElement, 'richText', nextRich)
                  }
                }}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="6"
                max="20000"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Weight</label>
              <select
                value={element.fontWeight || 'normal'}
                onChange={(e) => {
                  if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                    window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                    window.currentTextEditor.applyFormatting('fontWeight', e.target.value)
                  } else {
                    updateElementProperty(selectedElement, 'fontWeight', e.target.value)
                  }
                }}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="100">Thin</option>
                <option value="200">Extra Light</option>
                <option value="300">Light</option>
                <option value="normal">Regular</option>
                <option value="500">Medium</option>
                <option value="600">Semi Bold</option>
                <option value="bold">Bold</option>
                <option value="800">Extra Bold</option>
                <option value="900">Black</option>
              </select>
            </div>
          </div>

          {/* Style Buttons Row */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-600">Style</span>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handlePartialFormatting('bold', element.fontWeight !== 'bold')}
                className={`p-2 rounded-md border transition-all duration-200 ${element.fontWeight === 'bold'
                    ? 'bg-blue-500 border-blue-500 text-white shadow-md'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                title="Bold"
              >
                <Bold className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePartialFormatting('italic', element.fontStyle !== 'italic')}
                className={`p-2 rounded-md border transition-all duration-200 ${element.fontStyle === 'italic'
                    ? 'bg-blue-500 border-blue-500 text-white shadow-md'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                title="Italic"
              >
                <Italic className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePartialFormatting('underline', element.textDecoration !== 'underline')}
                className={`p-2 rounded-md border transition-all duration-200 ${element.textDecoration === 'underline'
                    ? 'bg-blue-500 border-blue-500 text-white shadow-md'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                  }`}
                title="Underline"
              >
                <Underline className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Text Fill */}
          <div>
            <label className="block text-xs text-gray-600 mb-2">Text Fill</label>
            <div className="space-y-3">
              {/* Fill Type Selector */}
              <div>
                <select
                  value={element.fillType || (element.color || element.fill ? 'solid' : 'none')}
                  onChange={(e) => {
                    const fillType = e.target.value
                    updateElementProperty(selectedElement, 'fillType', fillType)
                    if (fillType === 'none') {
                      if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                        window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                        window.currentTextEditor.applyFormatting('color', 'transparent')
                      } else {
                        updateElementProperty(selectedElement, 'color', 'transparent')
                        updateElementProperty(selectedElement, 'fill', 'transparent')
                      }
                    } else if (fillType === 'solid') {
                      const solidColor = element.solidColor || '#000000'
                      if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                        window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                        window.currentTextEditor.applyFormatting('color', solidColor)
                      } else {
                        updateElementProperty(selectedElement, 'color', solidColor)
                        updateElementProperty(selectedElement, 'fill', solidColor)
                      }
                    } else if (fillType === 'gradient') {
                      // Initialize gradient if not exists
                      if (!element.gradientStops) {
                        updateElementProperty(selectedElement, 'gradientStops', [
                          { color: '#000000', position: 0 },
                          { color: '#ffffff', position: 100 }
                        ])
                      }
                      if (!element.gradientType) {
                        updateElementProperty(selectedElement, 'gradientType', 'linear')
                      }
                      if (!element.gradientAngle) {
                        updateElementProperty(selectedElement, 'gradientAngle', 45)
                      }

                      // Apply gradient immediately
                      const stops = element.gradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }]
                      const gradientType = element.gradientType || 'linear'
                      const angle = element.gradientAngle || 45
                      const gradientCSS = gradientType === 'radial'
                        ? `radial-gradient(circle, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                        : `linear-gradient(${angle}deg, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`

                      // Store in format expected by rendering engine
                      updateElementProperty(selectedElement, 'fill', gradientCSS)
                      updateElementProperty(selectedElement, 'gradientConfig', {
                        type: gradientType,
                        angle: angle,
                        stops: stops
                      })
                    }
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="none">None</option>
                  <option value="solid">Solid Color</option>
                  <option value="gradient">Gradient Fill</option>
                </select>
              </div>

              {/* Solid Color Controls */}
              {(element.fillType === 'solid' || (!element.fillType && (element.color || element.fill))) && (
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={element.color || element.fill || element.solidColor || '#000000'}
                    onChange={(e) => {
                      const color = e.target.value
                      updateElementProperty(selectedElement, 'solidColor', color)
                      if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                        window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                        window.currentTextEditor.applyFormatting('color', color)
                      } else {
                        updateElementProperty(selectedElement, 'color', color)
                        updateElementProperty(selectedElement, 'fill', color)
                      }
                    }}
                    className="w-10 h-8 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={element.color || element.fill || element.solidColor || '#000000'}
                    onChange={(e) => {
                      const color = e.target.value
                      updateElementProperty(selectedElement, 'solidColor', color)
                      if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                        window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                        window.currentTextEditor.applyFormatting('color', color)
                      } else {
                        updateElementProperty(selectedElement, 'color', color)
                        updateElementProperty(selectedElement, 'fill', color)
                      }
                    }}
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="#000000"
                  />
                </div>
              )}

              {/* Gradient Controls */}
              {element.fillType === 'gradient' && (
                <div className="space-y-3 pl-4 border-l-2 border-blue-200">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Type</label>
                      <select
                        value={element.gradientType || 'linear'}
                        onChange={(e) => {
                          const gradientType = e.target.value
                          updateElementProperty(selectedElement, 'gradientType', gradientType)
                          // Update gradient immediately
                          const stops = element.gradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }]
                          const angle = element.gradientAngle || 45
                          const gradientCSS = gradientType === 'radial'
                            ? `radial-gradient(circle, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                            : `linear-gradient(${angle}deg, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`

                          // Store gradient for SVG/CSS use
                          updateElementProperty(selectedElement, 'fillGradient', gradientCSS)
                          updateElementProperty(selectedElement, 'textGradient', {
                            type: gradientType,
                            angle: angle,
                            stops: stops
                          })

                          // Apply gradient via CSS custom properties
                          // Store in format expected by rendering engine
                          updateElementProperty(selectedElement, 'fill', gradientCSS)
                          updateElementProperty(selectedElement, 'gradientConfig', {
                            type: gradientType,
                            angle: angle,
                            stops: stops
                          })
                        }}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="linear">Linear</option>
                        <option value="radial">Radial</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Angle</label>
                      <input
                        type="number"
                        value={element.gradientAngle || 45}
                        onChange={(e) => {
                          const angle = parseInt(e.target.value)
                          updateElementProperty(selectedElement, 'gradientAngle', angle)
                          // Update gradient immediately
                          const stops = element.gradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }]
                          const gradientType = element.gradientType || 'linear'
                          const gradientCSS = gradientType === 'radial'
                            ? `radial-gradient(circle, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                            : `linear-gradient(${angle}deg, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`

                          // Store gradient for SVG/CSS use
                          updateElementProperty(selectedElement, 'fillGradient', gradientCSS)
                          updateElementProperty(selectedElement, 'textGradient', {
                            type: gradientType,
                            angle: angle,
                            stops: stops
                          })

                          // Apply gradient via CSS custom properties
                          // Store in format expected by rendering engine
                          updateElementProperty(selectedElement, 'fill', gradientCSS)
                          updateElementProperty(selectedElement, 'gradientConfig', {
                            type: gradientType,
                            angle: angle,
                            stops: stops
                          })
                        }}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        max="360"
                        disabled={element.gradientType === 'radial'}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-xs text-gray-500">Color Stops</label>
                    {(element.gradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }]).map((stop, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="color"
                          value={stop.color}
                          onChange={(e) => {
                            const newStops = [...(element.gradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }])]
                            newStops[index] = { ...stop, color: e.target.value }
                            updateElementProperty(selectedElement, 'gradientStops', newStops)
                            // Update the CSS gradient
                            const gradientType = element.gradientType || 'linear'
                            const angle = element.gradientAngle || 45
                            const gradientCSS = gradientType === 'radial'
                              ? `radial-gradient(circle, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                              : `linear-gradient(${angle}deg, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`

                            // Apply gradient to element properties (this will be handled by the rendering engine)
                            updateElementProperty(selectedElement, 'fill', gradientCSS)
                            updateElementProperty(selectedElement, 'fillGradient', gradientCSS)
                            updateElementProperty(selectedElement, 'textGradient', {
                              type: gradientType,
                              angle: angle,
                              stops: newStops,
                              css: gradientCSS
                            })
                          }}
                          className="w-6 h-6 border border-gray-300 rounded cursor-pointer"
                        />
                        <input
                          type="number"
                          value={stop.position}
                          onChange={(e) => {
                            const newStops = [...(element.gradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }])]
                            newStops[index] = { ...stop, position: parseInt(e.target.value) }
                            updateElementProperty(selectedElement, 'gradientStops', newStops)
                            // Update the CSS gradient
                            const gradientType = element.gradientType || 'linear'
                            const angle = element.gradientAngle || 45
                            const gradientCSS = gradientType === 'radial'
                              ? `radial-gradient(circle, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                              : `linear-gradient(${angle}deg, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`

                            // Apply gradient to element properties (this will be handled by the rendering engine)
                            updateElementProperty(selectedElement, 'fill', gradientCSS)
                            updateElementProperty(selectedElement, 'fillGradient', gradientCSS)
                            updateElementProperty(selectedElement, 'textGradient', {
                              type: gradientType,
                              angle: angle,
                              stops: newStops,
                              css: gradientCSS
                            })
                          }}
                          className="w-16 px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                          min="0"
                          max="100"
                        />
                        <span className="text-xs text-gray-500">%</span>
                        {(element.gradientStops || []).length > 2 && (
                          <button
                            onClick={() => {
                              const newStops = (element.gradientStops || []).filter((_, i) => i !== index)
                              updateElementProperty(selectedElement, 'gradientStops', newStops)
                              // Update the CSS gradient
                              const gradientType = element.gradientType || 'linear'
                              const angle = element.gradientAngle || 45
                              const gradientCSS = gradientType === 'radial'
                                ? `radial-gradient(circle, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                                : `linear-gradient(${angle}deg, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`

                              if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                                window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                                window.currentTextEditor.applyFormatting('background', gradientCSS)
                                window.currentTextEditor.applyFormatting('color', 'transparent')
                                window.currentTextEditor.applyFormatting('backgroundClip', 'text')
                                window.currentTextEditor.applyFormatting('webkitBackgroundClip', 'text')
                              } else {
                                updateElementProperty(selectedElement, 'fill', gradientCSS)
                                updateElementProperty(selectedElement, 'color', gradientCSS)
                              }
                            }}
                            className="text-red-500 hover:text-red-700 text-xs"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    {(element.gradientStops || []).length < 5 && (
                      <button
                        onClick={() => {
                          const stops = element.gradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }]
                          const newStops = [...stops, { color: '#888888', position: 50 }]
                          updateElementProperty(selectedElement, 'gradientStops', newStops)
                          // Update the CSS gradient
                          const gradientType = element.gradientType || 'linear'
                          const angle = element.gradientAngle || 45
                          const gradientCSS = gradientType === 'radial'
                            ? `radial-gradient(circle, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                            : `linear-gradient(${angle}deg, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`

                          if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                            window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                            window.currentTextEditor.applyFormatting('background', gradientCSS)
                            window.currentTextEditor.applyFormatting('color', 'transparent')
                            window.currentTextEditor.applyFormatting('backgroundClip', 'text')
                            window.currentTextEditor.applyFormatting('webkitBackgroundClip', 'text')
                          } else {
                            updateElementProperty(selectedElement, 'fill', gradientCSS)
                            updateElementProperty(selectedElement, 'color', gradientCSS)
                          }
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        + Add Stop
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Font Weight */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Weight</label>
            <select
              value={element.fontWeight || 'normal'}
              onChange={(e) => {
                if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                  window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                  window.currentTextEditor.applyFormatting('fontWeight', e.target.value)
                } else {
                  updateElementProperty(selectedElement, 'fontWeight', e.target.value)
                }
              }}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="100">Thin</option>
              <option value="200">Extra Light</option>
              <option value="300">Light</option>
              <option value="normal">Regular</option>
              <option value="500">Medium</option>
              <option value="600">Semi Bold</option>
              <option value="bold">Bold</option>
              <option value="800">Extra Bold</option>
              <option value="900">Black</option>
            </select>
          </div>

          {/* Text Alignment */}
          <div>
            <label className="block text-xs text-gray-600 mb-2">Alignment</label>
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => {
                  if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                    window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                    window.currentTextEditor.applyFormatting('textAlign', 'left')
                  } else {
                    updateElementProperty(selectedElement, 'textAlign', 'left')
                  }
                }}
                className={`flex-1 p-2 rounded-md transition-all duration-200 ${element.textAlign === 'left'
                    ? 'bg-white shadow-sm border border-gray-300'
                    : 'hover:bg-gray-200'
                  }`}
                title="Align Left"
              >
                <AlignLeft className="w-4 h-4 mx-auto" />
              </button>
              <button
                onClick={() => {
                  if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                    window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                    window.currentTextEditor.applyFormatting('textAlign', 'center')
                  } else {
                    updateElementProperty(selectedElement, 'textAlign', 'center')
                  }
                }}
                className={`flex-1 p-2 rounded-md transition-all duration-200 ${element.textAlign === 'center' || !element.textAlign
                    ? 'bg-white shadow-sm border border-gray-300'
                    : 'hover:bg-gray-200'
                  }`}
                title="Align Center"
              >
                <AlignCenter className="w-4 h-4 mx-auto" />
              </button>
              <button
                onClick={() => {
                  if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                    window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                    window.currentTextEditor.applyFormatting('textAlign', 'right')
                  } else {
                    updateElementProperty(selectedElement, 'textAlign', 'right')
                  }
                }}
                className={`flex-1 p-2 rounded-md transition-all duration-200 ${element.textAlign === 'right'
                    ? 'bg-white shadow-sm border border-gray-300'
                    : 'hover:bg-gray-200'
                  }`}
                title="Align Right"
              >
                <AlignRight className="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>
        </div>

        {/* Position and Dimensions */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <div className="w-1 h-4 bg-purple-500 rounded"></div>
            <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Position & Dimensions</label>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">X Position</label>
                <input
                  type="number"
                  value={Math.round(element.x || 0)}
                  onChange={(e) => updateElementProperty(selectedElement, 'x', parseInt(e.target.value))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Y Position</label>
                <input
                  type="number"
                  value={Math.round(element.y || 0)}
                  onChange={(e) => updateElementProperty(selectedElement, 'y', parseInt(e.target.value))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Width</label>
                <input
                  type="number"
                  value={Math.round(element.width || 100)}
                  onChange={(e) => updateElementProperty(selectedElement, 'width', parseInt(e.target.value))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="10"
                  max="2000"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Height</label>
                <input
                  type="number"
                  value={Math.round(element.height || 50)}
                  onChange={(e) => updateElementProperty(selectedElement, 'height', parseInt(e.target.value))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="10"
                  max="2000"
                />
              </div>
            </div>

            {/* Fit to Page Button */}
            <FitToPageButton
              selectedElement={selectedElement}
              updateElementProperty={updateElementProperty}
              currentPage={currentPage}
              element={element}
            />
          </div>
        </div>

        {/* Spacing & Typography Details */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <div className="w-1 h-4 bg-indigo-500 rounded"></div>
            <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Spacing</label>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Line Height</label>
                <input
                  type="number"
                  value={element.lineHeight || 1.2}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value)
                    if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                      window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                      window.currentTextEditor.applyFormatting('lineHeight', v)
                    } else {
                      updateElementProperty(selectedElement, 'lineHeight', v)
                    }
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0.5"
                  max="3"
                  step="0.1"
                  placeholder="1.2"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Letter Spacing</label>
                <input
                  type="number"
                  value={element.letterSpacing || 0}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value)
                    if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                      window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                      window.currentTextEditor.applyFormatting('letterSpacing', v)
                    } else {
                      updateElementProperty(selectedElement, 'letterSpacing', v)
                    }
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="-5"
                  max="10"
                  step="0.1"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Word Spacing</label>
                <input
                  type="number"
                  value={element.wordSpacing || 0}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value)
                    if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                      window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                      window.currentTextEditor.applyFormatting('wordSpacing', v)
                    } else {
                      updateElementProperty(selectedElement, 'wordSpacing', v)
                    }
                  }}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="-10"
                  max="20"
                  step="0.1"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Kerning */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">Kerning</label>
              <select
                value={element.fontKerning || 'auto'}
                onChange={(e) => updateElementProperty(selectedElement, 'fontKerning', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="auto">Auto</option>
                <option value="normal">Normal</option>
                <option value="none">None</option>
              </select>
            </div>
          </div>
        </div>

        {/* Transform Section */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <div className="w-1 h-4 bg-orange-500 rounded"></div>
            <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Transform</label>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg space-y-3">
            {/* Rotation and Flip */}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Rotation</label>
                <div className="flex items-center space-x-1">
                  <input
                    type="number"
                    value={element.rotation || 0}
                    onChange={(e) => updateElementProperty(selectedElement, 'rotation', parseInt(e.target.value))}
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="-360"
                    max="360"
                  />
                  <span className="text-xs text-gray-500">°</span>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Flip H</label>
                <button
                  onClick={() => updateElementProperty(selectedElement, 'scaleX', (element.scaleX || 1) * -1)}
                  className={`w-full p-1.5 border border-gray-300 rounded hover:bg-gray-100 transition-colors ${(element.scaleX || 1) < 0 ? 'bg-gray-200' : ''
                    }`}
                  title="Flip Horizontally"
                >
                  <FlipHorizontal className="w-4 h-4 mx-auto" />
                </button>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Flip V</label>
                <button
                  onClick={() => updateElementProperty(selectedElement, 'scaleY', (element.scaleY || 1) * -1)}
                  className={`w-full p-1.5 border border-gray-300 rounded hover:bg-gray-100 transition-colors ${(element.scaleY || 1) < 0 ? 'bg-gray-200' : ''
                    }`}
                  title="Flip Vertically"
                >
                  <FlipVertical className="w-4 h-4 mx-auto" />
                </button>
              </div>
            </div>

            {/* Skew Controls */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Skew X</label>
                <input
                  type="number"
                  value={element.skewX || 0}
                  onChange={(e) => updateElementProperty(selectedElement, 'skewX', parseInt(e.target.value))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="-45"
                  max="45"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Skew Y</label>
                <input
                  type="number"
                  value={element.skewY || 0}
                  onChange={(e) => updateElementProperty(selectedElement, 'skewY', parseInt(e.target.value))}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="-45"
                  max="45"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Opacity */}
            <div>
              <label className="block text-xs text-gray-600 mb-2">Opacity: {Math.round((element.opacity || 1) * 100)}%</label>
              <input
                type="range"
                value={element.opacity || 1}
                onChange={(e) => updateElementProperty(selectedElement, 'opacity', parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                min="0"
                max="1"
                step="0.1"
              />
            </div>
          </div>
        </div>

        {/* Effects & Styling */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <div className="w-1 h-4 bg-pink-500 rounded"></div>
            <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Effects & Styling</label>
          </div>

          {/* Fill and Effects */}
          <div className="bg-gray-50 p-3 rounded-lg space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Pattern Fill</label>
              <select
                value={element.patternFill || 'none'}
                onChange={(e) => updateElementProperty(selectedElement, 'patternFill', e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="none">No Pattern</option>
                <option value="dots">Dots</option>
                <option value="stripes">Stripes</option>
                <option value="diagonal">Diagonal</option>
                <option value="grid">Grid</option>
              </select>
            </div>

            {/* Text Background */}
            <div>
              <label className="block text-xs text-gray-600 mb-2">Text Background</label>
              <div className="flex items-center space-x-2">
                <input
                  type="color"
                  value={element.textBackgroundColor || '#ffffff'}
                  onChange={(e) => {
                    const v = e.target.value
                    if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                      window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                      window.currentTextEditor.applyFormatting('backgroundColor', v)
                    } else {
                      updateElementProperty(selectedElement, 'textBackgroundColor', v)
                    }
                  }}
                  className="w-10 h-8 border border-gray-300 rounded cursor-pointer"
                  title="Background Color"
                />
                <input
                  type="text"
                  value={element.textBackgroundColor || 'transparent'}
                  onChange={(e) => updateElementProperty(selectedElement, 'textBackgroundColor', e.target.value)}
                  className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="transparent"
                />
                <button
                  onClick={() => updateElementProperty(selectedElement, 'textBackgroundColor', 'transparent')}
                  className="px-2 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Stroke Properties */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <div className="w-1 h-4 bg-yellow-500 rounded"></div>
            <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Stroke</label>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg space-y-3">
            {/* Stroke Fill Type */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">Stroke Fill</label>
              <select
                value={element.strokeFillType || (element.stroke && element.stroke !== 'none' ? 'solid' : 'none')}
                onChange={(e) => {
                  const fillType = e.target.value
                  updateElementProperty(selectedElement, 'strokeFillType', fillType)
                  if (fillType === 'none') {
                    updateElementProperty(selectedElement, 'stroke', 'none')
                  } else if (fillType === 'solid') {
                    updateElementProperty(selectedElement, 'stroke', element.strokeSolidColor || '#000000')
                  } else if (fillType === 'gradient') {
                    // Initialize gradient if not exists
                    if (!element.strokeGradientStops) {
                      updateElementProperty(selectedElement, 'strokeGradientStops', [
                        { color: '#000000', position: 0 },
                        { color: '#ffffff', position: 100 }
                      ])
                    }
                    if (!element.strokeGradientType) {
                      updateElementProperty(selectedElement, 'strokeGradientType', 'linear')
                    }
                    if (!element.strokeGradientAngle) {
                      updateElementProperty(selectedElement, 'strokeGradientAngle', 45)
                    }

                    // Apply gradient immediately
                    const stops = element.strokeGradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }]
                    const gradientType = element.strokeGradientType || 'linear'
                    const angle = element.strokeGradientAngle || 45
                    const gradientCSS = gradientType === 'radial'
                      ? `radial-gradient(circle, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                      : `linear-gradient(${angle}deg, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`

                    // Store gradient data for advanced renderers
                    updateElementProperty(selectedElement, 'strokeGradientConfig', {
                      type: gradientType,
                      angle: angle,
                      stops: stops,
                      css: gradientCSS
                    })
                    // For current rendering engine, use first color as fallback
                    const fallbackColor = stops[0]?.color || '#000000'
                    updateElementProperty(selectedElement, 'stroke', fallbackColor)
                  }
                }}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="none">None</option>
                <option value="solid">Solid Color</option>
                <option value="gradient">Gradient Fill</option>
              </select>
            </div>

            {/* Stroke Width */}
            <div>
              <label className="block text-xs text-gray-600 mb-1">Stroke Width</label>
              <input
                type="number"
                value={element.strokeWidth || 0}
                onChange={(e) => {
                  const v = parseInt(e.target.value)
                  if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                    window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                    window.currentTextEditor.applyFormatting('strokeWidth', v)
                  } else {
                    updateElementProperty(selectedElement, 'strokeWidth', v)
                  }
                }}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                max="20"
                placeholder="Width"
              />
            </div>

            {/* Solid Stroke Color */}
            {element.strokeFillType === 'solid' && (
              <div>
                <label className="block text-xs text-gray-600 mb-1">Stroke Color</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="color"
                    value={element.stroke || element.strokeSolidColor || '#000000'}
                    onChange={(e) => {
                      const color = e.target.value
                      updateElementProperty(selectedElement, 'strokeSolidColor', color)
                      if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                        window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                        window.currentTextEditor.applyFormatting('stroke', color)
                      } else {
                        updateElementProperty(selectedElement, 'stroke', color)
                      }
                    }}
                    className="w-10 h-8 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={element.stroke || element.strokeSolidColor || '#000000'}
                    onChange={(e) => {
                      const color = e.target.value
                      updateElementProperty(selectedElement, 'strokeSolidColor', color)
                      if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                        window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                        window.currentTextEditor.applyFormatting('stroke', color)
                      } else {
                        updateElementProperty(selectedElement, 'stroke', color)
                      }
                    }}
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="#000000"
                  />
                </div>
              </div>
            )}

            {/* Gradient Stroke */}
            {element.strokeFillType === 'gradient' && (
              <div className="space-y-3 pl-4 border-l-2 border-yellow-200">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Type</label>
                    <select
                      value={element.strokeGradientType || 'linear'}
                      onChange={(e) => {
                        const gradientType = e.target.value
                        updateElementProperty(selectedElement, 'strokeGradientType', gradientType)
                        // Update gradient immediately
                        const stops = element.strokeGradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }]
                        const angle = element.strokeGradientAngle || 45
                        const gradientCSS = gradientType === 'radial'
                          ? `radial-gradient(circle, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                          : `linear-gradient(${angle}deg, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`

                        // Store gradient data for advanced renderers
                        updateElementProperty(selectedElement, 'strokeGradientConfig', {
                          type: gradientType,
                          angle: angle,
                          stops: stops,
                          css: gradientCSS
                        })
                        // For current rendering engine, use first color as fallback
                        const fallbackColor = stops[0]?.color || '#000000'
                        updateElementProperty(selectedElement, 'stroke', fallbackColor)
                      }}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="linear">Linear</option>
                      <option value="radial">Radial</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Angle</label>
                    <input
                      type="number"
                      value={element.strokeGradientAngle || 45}
                      onChange={(e) => {
                        const angle = parseInt(e.target.value)
                        updateElementProperty(selectedElement, 'strokeGradientAngle', angle)
                        // Update gradient immediately
                        const stops = element.strokeGradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }]
                        const gradientType = element.strokeGradientType || 'linear'
                        const gradientCSS = gradientType === 'radial'
                          ? `radial-gradient(circle, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                          : `linear-gradient(${angle}deg, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`

                        // Store gradient data for advanced renderers
                        updateElementProperty(selectedElement, 'strokeGradientConfig', {
                          type: gradientType,
                          angle: angle,
                          stops: stops,
                          css: gradientCSS
                        })
                        // For current rendering engine, use first color as fallback
                        const fallbackColor = stops[0]?.color || '#000000'
                        updateElementProperty(selectedElement, 'stroke', fallbackColor)
                      }}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="0"
                      max="360"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs text-gray-500">Color Stops</label>
                  {(element.strokeGradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }]).map((stop, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={stop.color}
                        onChange={(e) => {
                          const newStops = [...(element.strokeGradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }])]
                          newStops[index] = { ...stop, color: e.target.value }
                          updateElementProperty(selectedElement, 'strokeGradientStops', newStops)
                          // Update the CSS gradient
                          const gradientType = element.strokeGradientType || 'linear'
                          const angle = element.strokeGradientAngle || 45
                          const gradientCSS = gradientType === 'radial'
                            ? `radial-gradient(circle, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                            : `linear-gradient(${angle}deg, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`

                          // Store gradient data for advanced renderers
                          updateElementProperty(selectedElement, 'strokeGradientConfig', {
                            type: gradientType,
                            angle: angle,
                            stops: newStops,
                            css: gradientCSS
                          })
                          // For current rendering engine, use first color as fallback
                          const fallbackColor = newStops[0]?.color || '#000000'
                          updateElementProperty(selectedElement, 'stroke', fallbackColor)
                        }}
                        className="w-6 h-6 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="number"
                        value={stop.position}
                        onChange={(e) => {
                          const newStops = [...(element.strokeGradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }])]
                          newStops[index] = { ...stop, position: parseInt(e.target.value) }
                          updateElementProperty(selectedElement, 'strokeGradientStops', newStops)
                          // Update the CSS gradient
                          const gradientType = element.strokeGradientType || 'linear'
                          const angle = element.strokeGradientAngle || 45
                          const gradientCSS = gradientType === 'radial'
                            ? `radial-gradient(circle, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                            : `linear-gradient(${angle}deg, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`

                          // Store gradient data for advanced renderers
                          updateElementProperty(selectedElement, 'strokeGradientConfig', {
                            type: gradientType,
                            angle: angle,
                            stops: newStops,
                            css: gradientCSS
                          })
                          // For current rendering engine, use first color as fallback
                          const fallbackColor = newStops[0]?.color || '#000000'
                          updateElementProperty(selectedElement, 'stroke', fallbackColor)
                        }}
                        className="w-16 px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                        max="100"
                      />
                      <span className="text-xs text-gray-500">%</span>
                      {(element.strokeGradientStops || []).length > 2 && (
                        <button
                          onClick={() => {
                            const newStops = (element.strokeGradientStops || []).filter((_, i) => i !== index)
                            updateElementProperty(selectedElement, 'strokeGradientStops', newStops)
                            // Update the CSS gradient
                            const gradientType = element.strokeGradientType || 'linear'
                            const angle = element.strokeGradientAngle || 45
                            const gradientCSS = gradientType === 'radial'
                              ? `radial-gradient(circle, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                              : `linear-gradient(${angle}deg, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`

                            updateElementProperty(selectedElement, 'stroke', gradientCSS)
                            updateElementProperty(selectedElement, 'strokeGradient', gradientCSS)
                            updateElementProperty(selectedElement, 'strokeGradientData', {
                              type: gradientType,
                              angle: angle,
                              stops: newStops,
                              css: gradientCSS
                            })
                          }}
                          className="text-red-500 hover:text-red-700 text-xs"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  {(element.strokeGradientStops || []).length < 5 && (
                    <button
                      onClick={() => {
                        const stops = element.strokeGradientStops || [{ color: '#000000', position: 0 }, { color: '#ffffff', position: 100 }]
                        const newStops = [...stops, { color: '#888888', position: 50 }]
                        updateElementProperty(selectedElement, 'strokeGradientStops', newStops)
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      + Add Stop
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Stroke Line Properties */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Line Join</label>
                <select
                  value={element.strokeLineJoin || 'miter'}
                  onChange={(e) => updateElementProperty(selectedElement, 'strokeLineJoin', e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="miter">Miter</option>
                  <option value="round">Round</option>
                  <option value="bevel">Bevel</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Line Cap</label>
                <select
                  value={element.strokeLineCap || 'butt'}
                  onChange={(e) => updateElementProperty(selectedElement, 'strokeLineCap', e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="butt">Butt</option>
                  <option value="round">Round</option>
                  <option value="square">Square</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Text Shadow */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <div className="w-1 h-4 bg-gray-500 rounded"></div>
            <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Text Shadow</label>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs text-gray-600">Enable Shadow</label>
              <input
                type="checkbox"
                checked={!!(element.textShadow?.enabled || element.shadowBlur > 0)}
                onChange={(e) => {
                  if (e.target.checked) {
                    const shadowConfig = {
                      enabled: true,
                      offsetX: element.shadowOffsetX || element.textShadow?.offsetX || 2,
                      offsetY: element.shadowOffsetY || element.textShadow?.offsetY || 2,
                      blur: element.shadowBlur || element.textShadow?.blur || 4,
                      color: element.shadowColor || element.textShadow?.color || '#000000',
                      opacity: element.textShadow?.opacity || 0.5,
                      gradient: element.textShadow?.gradient || null
                    }
                    if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                      window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                      window.currentTextEditor.applyFormatting('textShadow', shadowConfig)
                    } else {
                      updateElementProperty(selectedElement, 'textShadow', shadowConfig)
                    }
                  } else {
                    if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                      window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                      window.currentTextEditor.applyFormatting('textShadow', { enabled: false })
                    } else {
                      updateElementProperty(selectedElement, 'textShadow', { enabled: false })
                    }
                  }
                }}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
            </div>

            {(element.textShadow?.enabled || element.shadowBlur > 0) && (
              <div className="space-y-3 pl-4 border-l-2 border-gray-200">
                {/* Position Controls */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Offset X: {element.textShadow?.offsetX || element.shadowOffsetX || 2}px</label>
                    <input
                      type="range"
                      min="-20"
                      max="20"
                      value={element.textShadow?.offsetX || element.shadowOffsetX || 2}
                      onChange={(e) => {
                        const v = parseInt(e.target.value)
                        const shadowConfig = {
                          ...element.textShadow,
                          enabled: true,
                          offsetX: v
                        }
                        if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                          window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                          window.currentTextEditor.applyFormatting('textShadow', shadowConfig)
                        } else {
                          updateElementProperty(selectedElement, 'textShadow', shadowConfig)
                        }
                      }}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Offset Y: {element.textShadow?.offsetY || element.shadowOffsetY || 2}px</label>
                    <input
                      type="range"
                      min="-20"
                      max="20"
                      value={element.textShadow?.offsetY || element.shadowOffsetY || 2}
                      onChange={(e) => {
                        const v = parseInt(e.target.value)
                        const shadowConfig = {
                          ...element.textShadow,
                          enabled: true,
                          offsetY: v
                        }
                        if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                          window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                          window.currentTextEditor.applyFormatting('textShadow', shadowConfig)
                        } else {
                          updateElementProperty(selectedElement, 'textShadow', shadowConfig)
                        }
                      }}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>

                {/* Blur and Opacity */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Blur: {element.textShadow?.blur || element.shadowBlur || 4}px</label>
                    <input
                      type="range"
                      min="0"
                      max="20"
                      value={element.textShadow?.blur || element.shadowBlur || 4}
                      onChange={(e) => {
                        const v = parseInt(e.target.value)
                        const shadowConfig = {
                          ...element.textShadow,
                          enabled: true,
                          blur: v
                        }
                        if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                          window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                          window.currentTextEditor.applyFormatting('textShadow', shadowConfig)
                        } else {
                          updateElementProperty(selectedElement, 'textShadow', shadowConfig)
                        }
                      }}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Opacity: {Math.round((element.textShadow?.opacity !== undefined ? element.textShadow.opacity : 0.5) * 100)}%</label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={element.textShadow?.opacity !== undefined ? element.textShadow.opacity : 0.5}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value)
                        const shadowConfig = {
                          ...element.textShadow,
                          enabled: true,
                          opacity: v
                        }
                        if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                          window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                          window.currentTextEditor.applyFormatting('textShadow', shadowConfig)
                        } else {
                          updateElementProperty(selectedElement, 'textShadow', shadowConfig)
                        }
                      }}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>

                {/* Shadow Color Type */}
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Shadow Fill</label>
                  <select
                    value={element.textShadow?.fillType || 'solid'}
                    onChange={(e) => {
                      const fillType = e.target.value
                      if (fillType === 'gradient') {
                        // Initialize gradient if not exists
                        const stops = element.textShadow?.gradientStops || [{ color: '#000000', position: 0 }, { color: '#666666', position: 100 }]
                        const gradientType = element.textShadow?.gradientType || 'linear'
                        const angle = element.textShadow?.gradientAngle || 45
                        const offsetX = element.textShadow?.offsetX || 2
                        const offsetY = element.textShadow?.offsetY || 2
                        const blur = element.textShadow?.blur || 4
                        const opacity = element.textShadow?.opacity || 0.5

                        const shadowConfig = {
                          ...element.textShadow,
                          enabled: true,
                          fillType: fillType,
                          gradientStops: stops,
                          gradientType: gradientType,
                          gradientAngle: angle,
                          gradientCSS: gradientType === 'radial'
                            ? `radial-gradient(circle, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                            : `linear-gradient(${angle}deg, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`,
                          // Use first gradient color as fallback
                          color: stops[0]?.color || '#000000'
                        }
                        updateElementProperty(selectedElement, 'textShadow', shadowConfig)

                        // Store gradient data for advanced renderers  
                        updateElementProperty(selectedElement, 'shadowGradientConfig', {
                          type: gradientType,
                          angle: angle,
                          stops: stops,
                          css: shadowConfig.gradientCSS,
                          offsetX: offsetX,
                          offsetY: offsetY,
                          blur: blur,
                          opacity: opacity
                        })
                      } else {
                        const shadowConfig = {
                          ...element.textShadow,
                          enabled: true,
                          fillType: fillType
                        }
                        if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                          window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                          window.currentTextEditor.applyFormatting('textShadow', shadowConfig)
                        } else {
                          updateElementProperty(selectedElement, 'textShadow', shadowConfig)
                        }
                      }
                    }}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="solid">Solid Color</option>
                    <option value="gradient">Gradient Fill</option>
                  </select>
                </div>

                {/* Solid Shadow Color */}
                {(!element.textShadow?.fillType || element.textShadow?.fillType === 'solid') && (
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Shadow Color</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={element.textShadow?.color || element.shadowColor || '#000000'}
                        onChange={(e) => {
                          const v = e.target.value
                          const shadowConfig = {
                            ...element.textShadow,
                            enabled: true,
                            color: v,
                            solidColor: v
                          }
                          if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                            window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                            window.currentTextEditor.applyFormatting('textShadow', shadowConfig)
                          } else {
                            updateElementProperty(selectedElement, 'textShadow', shadowConfig)
                          }
                        }}
                        className="w-10 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={element.textShadow?.color || element.shadowColor || '#000000'}
                        onChange={(e) => {
                          const v = e.target.value
                          const shadowConfig = {
                            ...element.textShadow,
                            enabled: true,
                            color: v,
                            solidColor: v
                          }
                          if (window.currentTextEditor && window.currentTextEditor.applyFormatting) {
                            window.currentTextEditor.restoreSelection && window.currentTextEditor.restoreSelection()
                            window.currentTextEditor.applyFormatting('textShadow', shadowConfig)
                          } else {
                            updateElementProperty(selectedElement, 'textShadow', shadowConfig)
                          }
                        }}
                        className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                )}

                {/* Gradient Shadow */}
                {element.textShadow?.fillType === 'gradient' && (
                  <div className="space-y-3 pl-4 border-l-2 border-gray-300">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Type</label>
                        <select
                          value={element.textShadow?.gradientType || 'linear'}
                          onChange={(e) => {
                            const gradientType = e.target.value
                            const stops = element.textShadow?.gradientStops || [{ color: '#000000', position: 0 }, { color: '#666666', position: 100 }]
                            const angle = element.textShadow?.gradientAngle || 45
                            const offsetX = element.textShadow?.offsetX || 2
                            const offsetY = element.textShadow?.offsetY || 2
                            const blur = element.textShadow?.blur || 4
                            const opacity = element.textShadow?.opacity || 0.5

                            const shadowConfig = {
                              ...element.textShadow,
                              enabled: true,
                              gradientType: gradientType,
                              gradientCSS: gradientType === 'radial'
                                ? `radial-gradient(circle, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                                : `linear-gradient(${angle}deg, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`,
                              color: stops[0]?.color || '#000000'
                            }
                            updateElementProperty(selectedElement, 'textShadow', shadowConfig)

                            // Store gradient data for advanced renderers  
                            updateElementProperty(selectedElement, 'shadowGradientConfig', {
                              type: gradientType,
                              angle: angle,
                              stops: stops,
                              css: shadowConfig.gradientCSS,
                              offsetX: offsetX,
                              offsetY: offsetY,
                              blur: blur,
                              opacity: opacity
                            })
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="linear">Linear</option>
                          <option value="radial">Radial</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Angle</label>
                        <input
                          type="number"
                          value={element.textShadow?.gradientAngle || 45}
                          onChange={(e) => {
                            const angle = parseInt(e.target.value)
                            const gradientType = element.textShadow?.gradientType || 'linear'
                            const stops = element.textShadow?.gradientStops || [{ color: '#000000', position: 0 }, { color: '#666666', position: 100 }]
                            const offsetX = element.textShadow?.offsetX || 2
                            const offsetY = element.textShadow?.offsetY || 2
                            const blur = element.textShadow?.blur || 4
                            const opacity = element.textShadow?.opacity || 0.5

                            const shadowConfig = {
                              ...element.textShadow,
                              enabled: true,
                              gradientAngle: angle,
                              gradientCSS: gradientType === 'radial'
                                ? `radial-gradient(circle, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                                : `linear-gradient(${angle}deg, ${stops.map(s => `${s.color} ${s.position}%`).join(', ')})`,
                              color: stops[0]?.color || '#000000'
                            }
                            updateElementProperty(selectedElement, 'textShadow', shadowConfig)

                            // Store gradient data for advanced renderers  
                            updateElementProperty(selectedElement, 'shadowGradientConfig', {
                              type: gradientType,
                              angle: angle,
                              stops: stops,
                              css: shadowConfig.gradientCSS,
                              offsetX: offsetX,
                              offsetY: offsetY,
                              blur: blur,
                              opacity: opacity
                            })
                          }}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                          min="0"
                          max="360"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs text-gray-400">Color Stops</label>
                      {(element.textShadow?.gradientStops || [{ color: '#000000', position: 0 }, { color: '#666666', position: 100 }]).map((stop, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="color"
                            value={stop.color}
                            onChange={(e) => {
                              const newStops = [...(element.textShadow?.gradientStops || [{ color: '#000000', position: 0 }, { color: '#666666', position: 100 }])]
                              newStops[index] = { ...stop, color: e.target.value }
                              const shadowConfig = {
                                ...element.textShadow,
                                enabled: true,
                                gradientStops: newStops
                              }
                              updateElementProperty(selectedElement, 'textShadow', shadowConfig)
                            }}
                            className="w-6 h-6 border border-gray-300 rounded cursor-pointer"
                          />
                          <input
                            type="number"
                            value={stop.position}
                            onChange={(e) => {
                              const newStops = [...(element.textShadow?.gradientStops || [{ color: '#000000', position: 0 }, { color: '#666666', position: 100 }])]
                              newStops[index] = { ...stop, position: parseInt(e.target.value) }
                              const shadowConfig = {
                                ...element.textShadow,
                                enabled: true,
                                gradientStops: newStops
                              }
                              updateElementProperty(selectedElement, 'textShadow', shadowConfig)
                            }}
                            className="w-16 px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                            min="0"
                            max="100"
                          />
                          <span className="text-xs text-gray-400">%</span>
                          {(element.textShadow?.gradientStops || []).length > 2 && (
                            <button
                              onClick={() => {
                                const newStops = (element.textShadow?.gradientStops || []).filter((_, i) => i !== index)
                                const shadowConfig = {
                                  ...element.textShadow,
                                  enabled: true,
                                  gradientStops: newStops
                                }
                                updateElementProperty(selectedElement, 'textShadow', shadowConfig)
                              }}
                              className="text-red-500 hover:text-red-700 text-xs"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      {(element.textShadow?.gradientStops || []).length < 5 && (
                        <button
                          onClick={() => {
                            const stops = element.textShadow?.gradientStops || [{ color: '#000000', position: 0 }, { color: '#666666', position: 100 }]
                            const newStops = [...stops, { color: '#888888', position: 50 }]
                            // Generate gradient shadow CSS (this needs special handling)
                            const gradientType = element.textShadow?.gradientType || 'linear'
                            const angle = element.textShadow?.gradientAngle || 45
                            const offsetX = element.textShadow?.offsetX || 2
                            const offsetY = element.textShadow?.offsetY || 2
                            const blur = element.textShadow?.blur || 4
                            const opacity = element.textShadow?.opacity || 0.5

                            const shadowConfig = {
                              ...element.textShadow,
                              enabled: true,
                              gradientStops: newStops,
                              gradientCSS: gradientType === 'radial'
                                ? `radial-gradient(circle, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`
                                : `linear-gradient(${angle}deg, ${newStops.map(s => `${s.color} ${s.position}%`).join(', ')})`,
                              // For now, use the first color for actual shadow
                              color: newStops[0]?.color || '#000000'
                            }
                            updateElementProperty(selectedElement, 'textShadow', shadowConfig)

                            // Store gradient data for advanced renderers
                            updateElementProperty(selectedElement, 'shadowGradientConfig', {
                              type: gradientType,
                              angle: angle,
                              stops: newStops,
                              css: shadowConfig.gradientCSS,
                              offsetX: offsetX,
                              offsetY: offsetY,
                              blur: blur,
                              opacity: opacity
                            })
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          + Add Stop
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <div className="w-1 h-4 bg-cyan-500 rounded"></div>
            <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Advanced</label>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg space-y-3">
            {/* Text Wrapping */}
            <div>
              <label className="block text-xs text-gray-600 mb-2">Text Wrapping</label>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">
                  {element.wrap !== false ? 'Text wraps to multiple lines' : 'Font size adjusts to fit width'}
                </span>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500">Auto-resize</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={element.wrap !== false}
                      onChange={(e) => updateElementProperty(selectedElement, 'wrap', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <span className="text-xs text-gray-500">Wrap</span>
                </div>
              </div>
              <div className="mt-1 text-xs text-gray-500">
                {element.wrap !== false 
                  ? 'Text will wrap to multiple lines when it exceeds the container width'
                  : 'Font size will be automatically reduced to fit text within the container width'
                }
              </div>
            </div>

            {/* Vertical Alignment and Direction */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Vertical Align</label>
                <select
                  value={element.verticalAlign || 'middle'}
                  onChange={(e) => updateElementProperty(selectedElement, 'verticalAlign', e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="top">Top</option>
                  <option value="middle">Middle</option>
                  <option value="bottom">Bottom</option>
                  <option value="baseline">Baseline</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Direction</label>
                <select
                  value={element.direction || 'ltr'}
                  onChange={(e) => updateElementProperty(selectedElement, 'direction', e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ltr">LTR</option>
                  <option value="rtl">RTL</option>
                </select>
              </div>
            </div>

            {/* Additional Text Decorations */}
            <div>
              <label className="block text-xs text-gray-600 mb-2">Text Decorations</label>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => updateElementProperty(selectedElement, 'textDecoration', element.textDecoration?.includes('overline') ? element.textDecoration.replace('overline', '').trim() || 'none' : (element.textDecoration || '') + ' overline')}
                  className={`px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors ${element.textDecoration?.includes('overline') ? 'bg-gray-200' : ''}`}
                  title="Overline"
                >
                  O̅verline
                </button>
                <button
                  onClick={() => updateElementProperty(selectedElement, 'textDecoration', element.textDecoration?.includes('line-through') ? element.textDecoration.replace('line-through', '').trim() || 'none' : (element.textDecoration || '') + ' line-through')}
                  className={`px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors ${element.textDecoration?.includes('line-through') ? 'bg-gray-200' : ''}`}
                  title="Strikethrough"
                >
                  S̶trike
                </button>
              </div>
            </div>

            {/* Blend Mode and Clip Path */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Blend Mode</label>
                <select
                  value={element.blendMode || 'normal'}
                  onChange={(e) => updateElementProperty(selectedElement, 'blendMode', e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="normal">Normal</option>
                  <option value="multiply">Multiply</option>
                  <option value="screen">Screen</option>
                  <option value="overlay">Overlay</option>
                  <option value="darken">Darken</option>
                  <option value="lighten">Lighten</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Clip Path</label>
                <select
                  value={element.clipPath || 'none'}
                  onChange={(e) => updateElementProperty(selectedElement, 'clipPath', e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="none">No Clip</option>
                  <option value="circle(50%)">Circle</option>
                  <option value="ellipse(50% 25%)">Ellipse</option>
                  <option value="polygon(50% 0%, 0% 100%, 100% 100%)">Triangle</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Actions */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <div className="w-1 h-4 bg-red-500 rounded"></div>
            <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Actions</label>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg space-y-3">
            {/* Text Split Actions */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => splitTextIntoWords(selectedElement)}
                className="flex items-center justify-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm transition-colors"
                title="Break text into words"
              >
                <Split className="w-4 h-4" />
                <span>Split Words</span>
              </button>
              <button
                onClick={() => splitTextIntoCharacters(selectedElement)}
                className="flex items-center justify-center space-x-1 px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm transition-colors"
                title="Break text into characters"
              >
                <Scissors className="w-4 h-4" />
                <span>Split Chars</span>
              </button>
            </div>

            {/* Convert to Outlines */}
            <button
              onClick={() => {
                if (window.confirm('Convert this text to SVG outlines? This action is irreversible.')) {
                  window.editorActions?.convertTextToSVGGraphic?.(selectedElement)
                }
              }}
              className="w-full px-3 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 text-sm transition-colors"
              title="Convert text to SVG outlines (irreversible)"
            >
              Convert to Outlines
            </button>
          </div>
        </div>
      </div>

      {/* Animation Properties */}
      <AnimationPropertiesPanel
        element={element}
        selectedElement={selectedElement}
        updateElementProperty={updateElementProperty}
      />
    </div>
  )
}

export default TextElementProperties
